import { Grid, Stack, Typography } from '@mui/material';

import { useState, useEffect, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import AccordionComp from 'common/components/Accordion/AccordionComp';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import PromptSelect from 'common/components/PromptSelect/PromptSelect';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { AI_SIMULATION_MODEL_KEYS } from 'common/constants/AISimulationConstants';
import { PROGRESS_BAR_ELEMENTS } from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setRegInvSummaryRegenerated,
	setSummaryTextForReview,
} from 'common/store/reducer/regulationInventoryReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { generateAISimulationPromptResult } from 'features/AiSimulation/api';
import { generateSummary, editSummary } from 'features/RegulationInventory/api';
import {
	ICitationDetailsElements,
	IGroupCitationDetails,
} from 'features/RegulationInventory/api/type-def';
import { fetchSummarayText } from 'features/RegulationInventory/RegInventoryUtils';

import SummaryDetails from './SummaryDetails';

import styles from './SummaryDetails.module.scss';

const { OK, CREATED, INTERNAL_SERVER_ERROR } = HTTP_STATUS_CODES;

const { SUMMARY_ASSESSMENT, SUMMARY_REVIEW, MAPPING_REVIEW } =
	PROGRESS_BAR_ELEMENTS;

interface ISummaryProps {
	summaryAssessment: IGroupCitationDetails | undefined;
	groupId: string | undefined;
	enable: boolean;
	status?: string;
}

const SummaryLayout = (props: ISummaryProps) => {
	const dispatch = useDispatch();

	const { summaryAssessment, groupId, enable = false, status } = props;
	const summaryGrouping: ICitationDetailsElements[] =
		summaryAssessment?.grouping || [];
	const [summaryExpanded, setSummaryExpanded] = useState(false);
	const [isSummaryAvailable, setIsSummaryAvailable] = useState(false);
	const [isSummaryEditable, setIsSummaryEditable] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [udpatedSummary, setUpdatedSummary] = useState('');
	const { OBLIGATION_GENERATION_KEY } = AI_SIMULATION_MODEL_KEYS;
	const summaryText: any = useSelector(
		(state: RootState) => state?.regulationInventory?.summaryTextForReview,
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	useEffect(() => {
		if (summaryText) {
			setIsSummaryAvailable(true);
			setUpdatedSummary(summaryText);
		} else {
			setIsSummaryAvailable(false);
		}
	}, [summaryText]);

	const generatePromptSelection = async (event: any) => {
		const promptKey = event.key;
		generateSummaryDetails(promptKey);
	};
	const generateSummaryHandler = async (event: any, isRegenerate: boolean) => {
		event.stopPropagation();
		if (isRegenerate) dispatch(setRegInvSummaryRegenerated(true));
		generateSummaryDetails('');
	};

	const generateSummaryDetails = async (promptKey: string) => {
		setIsSummaryAvailable(false);
		setIsLoading(true);
		if (groupId) {
			const text = fetchSummarayText(summaryGrouping);
			const resp: any = promptKey.length
				? await generateAISimulationPromptResult(promptKey, text, true)
				: await generateSummary({ citation: text }, groupId);
			if (resp?.statusCode === OK || resp?.statusCode === CREATED) {
				setIsLoading(false);
				if (resp?.responseBody?.answer) {
					setIsSummaryAvailable(true);
					setIsSummaryEditable(true);
					setSummaryExpanded(true);
					setUpdatedSummary(resp?.responseBody?.answer);
					dispatch(
						setToastMessage({
							message: 'Successfully generated Summary!',
							severity: 'success',
							isToastOpen: true,
						}),
					);
				}
			} else if (resp?.statusCode === INTERNAL_SERVER_ERROR) {
				setIsLoading(false);
				dispatch(
					setToastMessage({
						message: 'Unable to Generate Summary',
						severity: 'error',
						isToastOpen: true,
					}),
				);
			} else {
				setIsLoading(false);
				const { errorBody } = resp;
				const responseError: any = resp.error;
				const errorMsg =
					(errorBody?.errorMessage as string) ||
					(responseError?.message as string);
				dispatch(
					setToastMessage({
						message: errorMsg,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		}
	};

	const toggleEditHandler = (event: any) => {
		event.stopPropagation();
		setIsSummaryEditable(!isSummaryEditable);
	};

	const toggleCancelHandler = (event: any) => {
		event.stopPropagation();
		setIsSummaryEditable(!isSummaryEditable);
		if (summaryText !== udpatedSummary) {
			setUpdatedSummary(summaryText);
		}
		if (!summaryText) {
			setIsSummaryAvailable(false);
		}
	};

	const summarySaveHandler = async (event: any) => {
		event.stopPropagation();
		setIsSummaryEditable(false);
		setIsLoading(true);
		if (groupId) {
			const resp: any = await editSummary(
				{ answer: udpatedSummary || summaryText },
				groupId,
			);
			if (resp?.statusCode === OK || resp?.statusCode === CREATED) {
				setIsLoading(false);
				dispatch(setSummaryTextForReview(resp?.responseBody?.answer));
				if (resp?.responseBody?.answer) {
					setIsSummaryAvailable(true);
					setUpdatedSummary(resp?.responseBody?.answer);
					dispatch(
						setToastMessage({
							message: 'Successfully updated Summary!',
							severity: 'success',
							isToastOpen: true,
						}),
					);
				} else {
					setIsLoading(false);
					const { errorBody } = resp;
					const responseError: any = resp.error;
					const errorMsg =
						(errorBody?.errorMessage as string) ||
						(responseError?.message as string);
					dispatch(
						setToastMessage({
							message: errorMsg,
							severity: 'error',
							isToastOpen: true,
						}),
					);
				}
			}
		}
	};

	const udpateSummaryHandler = (editedSummary: string) => {
		setUpdatedSummary(editedSummary);
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	return (
		<>
			{isSummaryAvailable ? (
				<AccordionComp
					isLeftAccordian
					className={styles['summary-accordion']}
					expanded={summaryExpanded}
					onChange={() => setSummaryExpanded(!summaryExpanded)}
					summary={
						<Grid container display="flex" justifyContent="space-between">
							<Grid item display="flex">
								<Typography
									className={styles['summary-accordian-text-header']}
									data-testid="summary-test-id"
									onChange={() => setSummaryExpanded(!summaryExpanded)}
								>
									Summary
								</Typography>
							</Grid>
							{enable && (
								<Grid item>
									<Stack direction="row" spacing={2}>
										{isSummaryEditable && (
											<ButtonComp
												btnType="secondary"
												disabled={isLoading}
												loading={isLoading}
												clickHandler={toggleCancelHandler}
											>
												Cancel
											</ButtonComp>
										)}
										{!isSummaryEditable && (
											<PromptSelect
												aiModelKey={OBLIGATION_GENERATION_KEY}
												btnType="secondary"
												disabled={isLoading}
												loading={isLoading}
												onSelectPrompt={generatePromptSelection}
												clickHandler={(event: any) =>
													generateSummaryHandler(event, true)
												}
											>
												Regenerate Summary
											</PromptSelect>
										)}
										<ButtonComp
											btnType="primary"
											disabled={isLoading || !udpatedSummary}
											clickHandler={
												isSummaryEditable
													? summarySaveHandler
													: toggleEditHandler
											}
										>
											{isSummaryEditable ? 'Save' : 'Edit'}
										</ButtonComp>
									</Stack>
								</Grid>
							)}
						</Grid>
					}
					details={
						<SummaryDetails
							summaryText={udpatedSummary}
							isEditable={isSummaryEditable}
							isLoading={isLoading}
							udpateSummaryHandler={udpateSummaryHandler}
						/>
					}
				/>
			) : (
				<div className={styles['summary-container']}>
					<Grid container display="flex" justifyContent="space-between">
						<Grid item display="flex" className={styles['summary-text-header']}>
							Summary
						</Grid>
						{!isSummaryAvailable &&
							(status === MAPPING_REVIEW || status === SUMMARY_REVIEW) && (
								<Loader show className={styles['no-data']} />
							)}
						{!summaryGrouping && <Loader show className={styles['no-data']} />}
						{summaryGrouping?.length === 0 && (
							<p className={styles['no-data']}>No content available</p>
						)}
						{summaryGrouping?.length > 0 && status === SUMMARY_ASSESSMENT && (
							<Grid item style={{ margin: '10px' }}>
								<PromptSelect
									aiModelKey={OBLIGATION_GENERATION_KEY}
									btnType="primary"
									loading={isLoading}
									data-testid="summary-regenerate-btn"
									onSelectPrompt={generatePromptSelection}
									clickHandler={(event: any) =>
										generateSummaryHandler(event, false)
									}
								>
									{toSentenceCase('Generate Summary')}
								</PromptSelect>
							</Grid>
						)}
					</Grid>
				</div>
			)}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</>
	);
};

export default SummaryLayout;
